// Employee Profile — "Employee All History" card: every finalized/in-progress activity record.
import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { EmployeeAllHistoryRow } from '../types';
import ProfileDataTable, { ProfileTableColumn } from './ProfileDataTable';

interface EmployeeAllHistoryCardProps {
  rows: EmployeeAllHistoryRow[];
  onResume?: (row: EmployeeAllHistoryRow) => void;
  onView?: (row: EmployeeAllHistoryRow) => void;
  /** Opens the Employee Profile screen for the clicked Employee Name cell. */
  onOpenEmployee?: (employeeName: string, employeeId?: string, employeeRole?: string) => void;
}

const StatusPill: React.FC<{ status: EmployeeAllHistoryRow['status'] }> = ({ status }) => {
  const tone =
    status === 'Completed' ? { bg: AdminColors.green75, fg: AdminColors.green600 } :
    status === 'Missed' ? { bg: AdminColors.red75, fg: AdminColors.red700 } :
    { bg: AdminColors.yellow50, fg: AdminColors.textWarning };
  return (
    <View style={[styles.pill, { backgroundColor: tone.bg }]}>
      <Text style={[styles.pillText, { color: tone.fg }]}>{status}</Text>
    </View>
  );
};

const EmployeeAllHistoryCard: React.FC<EmployeeAllHistoryCardProps> = ({ rows, onResume, onView, onOpenEmployee }) => {
  const columns: ProfileTableColumn<EmployeeAllHistoryRow>[] = [
    {
      key: 'employeeName', label: 'Employee Name', flex: 1.2, sortable: true,
      render: row => (
        <Pressable
          onPress={() => onOpenEmployee?.(row.employeeName, undefined, row.role)}
          hitSlop={4}
        >
          <Text style={styles.nameLink}>{row.employeeName}</Text>
        </Pressable>
      ),
    },
    { key: 'role', label: 'Role', flex: 1, filterable: true },
    { key: 'type', label: 'Type', flex: 1.2, sortable: true, filterable: true },
    { key: 'result', label: 'Result', flex: 0.9 },
    { key: 'createdOn', label: 'Created On', flex: 1, sortable: true },
    { key: 'createdBy', label: 'Created By', flex: 1 },
    { key: 'finalized', label: 'Finalized', flex: 1, getValue: r => r.finalized || '—' },
    { key: 'periodWeek', label: 'Period / Week', flex: 1 },
    {
      key: 'status', label: 'Status', flex: 0.9, sortable: true, filterable: true,
      render: row => <StatusPill status={row.status} />,
    },
    {
      key: 'action', label: '', flex: 0.8,
      render: row => (
        <Pressable
          style={styles.actionButton}
          onPress={() => (row.status === 'In Progress' ? onResume?.(row) : onView?.(row))}
        >
          <Text style={styles.actionButtonText}>{row.status === 'In Progress' ? 'Resume' : 'View'}</Text>
        </Pressable>
      ),
    },
  ];

  return (
    <View style={styles.card}>
      <Text style={styles.title}>Employee All History</Text>
      <ProfileDataTable
        columns={columns}
        rows={rows}
        searchPlaceholder="Search all history…"
        searchFields={['employeeName', 'type', 'result']}
        emptyLabel="No activity history found for this employee."
      />
    </View>
  );
};

const styles = StyleSheet.create({
  nameLink: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    color: AdminColors.blue700,
  },
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    padding: 20,
    gap: 12,
  },
  title: {
    fontFamily: FontFamily.bold,
    fontSize: 15,
    color: AdminColors.textPrimary,
  },
  pill: {
    borderRadius: 10,
    paddingVertical: 2,
    paddingHorizontal: 8,
    alignSelf: 'flex-start',
  },
  pillText: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
  },
  actionButton: {
    borderWidth: 1,
    borderColor: AdminColors.blue700,
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 10,
    alignSelf: 'flex-start',
  },
  actionButtonText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: AdminColors.blue700,
  },
});

export default EmployeeAllHistoryCard;
